import React, { useContext, useEffect, useRef, useState } from 'react'
import { KlaviyoLogger } from '../components/pixels/KlaviyoPixel'
import type { AnalyticsTracker } from '../analytics/tracking'
import { AnalyticsContext } from '../context/analyticsContext'
import { CollectionPlayerVideo, PlaylistPlayerVideo } from '../types'

interface ProgressMilestone {
    isReached: (progressPercent: number) => boolean
    track: (analytics: AnalyticsTracker, video: TrackedVideo) => void
}

type TrackedVideo = PlaylistPlayerVideo | CollectionPlayerVideo

/** Ordered milestones; each fires once, in order, as playback progresses. */
const PROGRESS_MILESTONES: ProgressMilestone[] = [
    {
        isReached: (percent) => percent > 0,
        track: (analytics, video) => analytics.TRACK_VIDEO_IMPRESSION(video),
    },
    {
        isReached: (percent) => percent >= 25,
        track: (analytics, video) =>
            analytics.TRACK_VIDEO_FIRST_QUARTILE(video),
    },
    {
        isReached: (percent) => percent >= 50,
        track: (analytics, video) => analytics.TRACK_VIDEO_MIDPOINT(video),
    },
    {
        isReached: (percent) => percent >= 75,
        track: (analytics, video) =>
            analytics.TRACK_VIDEO_THIRD_QUARTILE(video),
    },
    {
        isReached: (percent) => percent >= 95,
        track: (analytics, video) => analytics.TRACK_VIDEO_COMPLETE(video),
    },
]

export const useVideoProgressTracker = (
    video: TrackedVideo,
    videoRef: React.RefObject<HTMLVideoElement>,
) => {
    const [videoSeconds, setVideoSeconds] = useState(0)
    const [progressPercent, setProgressPercent] = useState(0)
    const { Analytics } = useContext(AnalyticsContext)
    // Read from the interval so a new tracker instance doesn't restart tracking.
    const analyticsRef = useRef(Analytics)
    useEffect(() => {
        analyticsRef.current = Analytics
    }, [Analytics])

    const scrubByPercent = (percent: number) => {
        const videoElement = videoRef.current
        if (!videoElement) return
        const duration = videoElement.duration
        const seconds = Math.round((percent / 100) * duration)
        videoElement.currentTime = seconds
    }

    useEffect(() => {
        const videoElement = videoRef.current
        if (!videoElement) return

        // Number of milestones already reported for this video
        let reachedMilestones = 0

        // Use 30fps interval for completely fluid progress bar updates
        const intervalId = setInterval(() => {
            if (!videoElement.paused && !videoElement.ended) {
                const duration = videoElement.duration
                if (duration && duration > 0) {
                    const percent = (videoElement.currentTime / duration) * 100
                    setProgressPercent(percent)

                    if (percent === 0) reachedMilestones = 0
                    while (
                        reachedMilestones < PROGRESS_MILESTONES.length &&
                        PROGRESS_MILESTONES[reachedMilestones].isReached(
                            percent,
                        )
                    ) {
                        PROGRESS_MILESTONES[reachedMilestones].track(
                            analyticsRef.current,
                            video,
                        )
                        reachedMilestones++
                    }
                }
            }
            setVideoSeconds(Math.round(videoElement.currentTime))
        }, 1000 / 30) // 30 fps = ~33ms interval

        return () => {
            clearInterval(intervalId)
            setVideoSeconds(0)
            setProgressPercent(0)
        }
    }, [video])

    useEffect(() => {
        switch (videoSeconds) {
            case 3:
                Analytics.TRACK_VIDEO_SECONDS(video, 3)
                break
            case 6:
                Analytics.TRACK_VIDEO_SECONDS(video, 6)
                KlaviyoLogger.TrackVideo6SecondView(video)
                break
            case 10:
                Analytics.TRACK_VIDEO_SECONDS(video, 10)
                KlaviyoLogger.TrackVideo10SecondView(video)
                break
            case 10:
                Analytics.TRACK_VIDEO_SECONDS(video, 10)
                break
        }
    }, [video, videoSeconds])

    return {
        progressPercent,
        scrubByPercent,
    }
}
